/**
 * Job Presentation
 *
 * Standalone presentation of onboarding slides for a completed ad-generator job.
 * Shows the same slides as the onboarding flow but without requiring the OnboardingContext.
 */

import { useEffect, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import { cn } from '@trafiflow/shared-ui'
import { adGeneratorService } from '@/services/ad-generator'
import { competitorAnalysisService } from '@/services/competitor-analysis'
import type { AdGenJob } from '@/services/ad-generator/types'
import {
  BrandRevealSlide,
  MetaIdentitySlide,
  AdsCollectionSlide,
  CompetitorSlide,
  PatternsSlide,
  StrategySlide,
  CreativeBlueprintSlide,
  ProductBibleSlide,
  ClipBankSlide,
  FinalAdSlide,
} from '../../onboarding/slides'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

const TOTAL_SLIDES = 10

interface JobPresentationProps {
  job: AdGenJob
  onClose: () => void
}

export default function JobPresentation({ job, onClose }: JobPresentationProps) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [artifacts, setArtifacts] = useState<OnboardingArtifacts>({
    brand: null,
    metaIdentity: null,
    adsCollection: null,
    patterns: null,
    strategy: null,
    blueprint: null,
    clipBank: null,
    winner: job.winner || null,
    competitorAnalysis: null,
    productBible: null,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [loadingStage, setLoadingStage] = useState('Initializing...')

  // Load artifacts when component mounts
  useEffect(() => {
    loadArtifacts()
  }, [job.job_id])

  const loadArtifacts = async () => {
    setIsLoading(true)
    setLoadingStage('Loading brand data...')

    const loadedArtifacts: OnboardingArtifacts = {
      brand: null,
      metaIdentity: null,
      adsCollection: null,
      patterns: null,
      strategy: null,
      blueprint: null,
      clipBank: null,
      winner: job.winner || null,
      competitorAnalysis: null,
      productBible: null,
    }

    // Load all artifacts in parallel
    setLoadingStage('Fetching pipeline artifacts...')
    const [brandResult, metaResult, adsResult, patternsResult, strategyResult, blueprintResult, clipBankResult, productBibleResult] = await Promise.allSettled([
      adGeneratorService.getStageDetail(job.job_id, 'brand_discovery'),
      adGeneratorService.getStageDetail(job.job_id, 'meta_identity_resolution'),
      adGeneratorService.getStageDetail(job.job_id, 'brand_ad_collection'),
      adGeneratorService.getStageDetail(job.job_id, 'competitor_intelligence'),
      adGeneratorService.getStageDetail(job.job_id, 'strategy_selection'),
      adGeneratorService.getStageDetail(job.job_id, 'creative_blueprint'),
      adGeneratorService.getStageDetail(job.job_id, 'clip_bank_creation'),
      adGeneratorService.getStageDetail(job.job_id, 'product_bible'),
    ])

    if (brandResult.status === 'fulfilled') {
      loadedArtifacts.brand = brandResult.value.artifact || null
    }
    if (metaResult.status === 'fulfilled') {
      loadedArtifacts.metaIdentity = metaResult.value.artifact || null
    }
    if (adsResult.status === 'fulfilled') {
      loadedArtifacts.adsCollection = adsResult.value.artifact || null
    }
    if (patternsResult.status === 'fulfilled') {
      loadedArtifacts.patterns = patternsResult.value.artifact || null
    }
    if (strategyResult.status === 'fulfilled') {
      loadedArtifacts.strategy = strategyResult.value.artifact || null
    }
    if (blueprintResult.status === 'fulfilled') {
      loadedArtifacts.blueprint = blueprintResult.value.artifact || null
    }
    if (clipBankResult.status === 'fulfilled') {
      loadedArtifacts.clipBank = clipBankResult.value.artifact || null
    }
    if (productBibleResult.status === 'fulfilled') {
      loadedArtifacts.productBible = productBibleResult.value.artifact || null
    }

    // Load existing competitor analysis (don't create new jobs)
    setLoadingStage('Loading competitor data...')
    try {
      const competitorResult = await loadCompetitorAnalysis(job.url)
      if (competitorResult) {
        loadedArtifacts.competitorAnalysis = competitorResult
      }
    } catch (err) {
      console.warn('[JobPresentation] Failed to load competitor analysis:', err)
    }

    setLoadingStage('Preparing presentation...')
    setArtifacts(loadedArtifacts)
    setIsLoading(false)
  }

  // Load existing competitor analysis (don't create new ones - that happens during onboarding)
  const loadCompetitorAnalysis = async (brandUrl: string) => {
    try {
      const jobsResponse = await competitorAnalysisService.listJobs()

      // First check for completed job
      const existingJob = jobsResponse.jobs.find(
        j => j.status === 'completed' && j.brand_url === brandUrl && j.result
      )
      if (existingJob?.result) {
        console.log('[JobPresentation] Found existing competitor analysis:', existingJob.job_id)
        return existingJob.result
      }

      // If there's a running job, wait for it (but don't create new ones)
      const runningJob = jobsResponse.jobs.find(
        j => (j.status === 'running' || j.status === 'pending') && j.brand_url === brandUrl
      )
      if (runningJob) {
        console.log('[JobPresentation] Found running competitor analysis, waiting:', runningJob.job_id)
        return await pollCompetitorJob(runningJob.job_id)
      }

      console.log('[JobPresentation] No competitor analysis found for brand')
      return null
    } catch (err) {
      console.warn('[JobPresentation] Failed to load competitor analysis:', err)
      return null
    }
  }

  // Poll competitor analysis job until completion (only for already running jobs)
  const pollCompetitorJob = async (jobId: string, maxAttempts = 30) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const job = await competitorAnalysisService.getJob(jobId)
        if (job.status === 'completed' && job.result) {
          console.log('[JobPresentation] Competitor analysis completed:', jobId)
          return job.result
        }
        if (job.status === 'failed' || job.status === 'cancelled') {
          console.warn('[JobPresentation] Competitor analysis failed:', job.status)
          return null
        }
        // Wait 2 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2000))
      } catch (err) {
        console.error('[JobPresentation] Error polling competitor analysis:', err)
        return null
      }
    }
    console.warn('[JobPresentation] Competitor analysis timed out')
    return null
  }

  // Keyboard navigation (arrow keys only, no Escape to prevent accidental close)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') {
      e.preventDefault()
      setCurrentSlideIndex(prev => Math.min(prev + 1, TOTAL_SLIDES - 1))
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault()
      setCurrentSlideIndex(prev => Math.max(prev - 1, 0))
    }
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const nextSlide = () => setCurrentSlideIndex(prev => Math.min(prev + 1, TOTAL_SLIDES - 1))
  const prevSlide = () => setCurrentSlideIndex(prev => Math.max(prev - 1, 0))

  const handleComplete = () => {
    onClose()
  }

  const renderSlide = () => {
    switch (currentSlideIndex) {
      case 0:
        return <BrandRevealSlide key="brand" artifacts={artifacts} />
      case 1:
        return <MetaIdentitySlide key="meta" artifacts={artifacts} />
      case 2:
        return <AdsCollectionSlide key="ads" artifacts={artifacts} />
      case 3:
        return <CompetitorSlide key="competitor" artifacts={artifacts} />
      case 4:
        return <PatternsSlide key="patterns" artifacts={artifacts} />
      case 5:
        return <StrategySlide key="strategy" artifacts={artifacts} />
      case 6:
        return <CreativeBlueprintSlide key="blueprint" artifacts={artifacts} />
      case 7:
        return <ProductBibleSlide key="productbible" artifacts={artifacts} />
      case 8:
        return <ClipBankSlide key="clipbank" artifacts={artifacts} />
      case 9:
        return <FinalAdSlide key="final" artifacts={artifacts} onComplete={handleComplete} />
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Animated icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center mx-auto mb-6 shadow-lg"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            >
              <Sparkles className="w-10 h-10 text-white" />
            </motion.div>
          </motion.div>

          {/* Loading text */}
          <motion.h3
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-semibold text-tf-text mb-2"
          >
            Preparing Your Presentation
          </motion.h3>

          <motion.p
            key={loadingStage}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-tf-text-muted mb-6"
          >
            {loadingStage}
          </motion.p>

          {/* Progress bar */}
          <div className="w-48 h-1.5 bg-tf-bg-muted rounded-full mx-auto overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{ duration: 8, ease: 'easeInOut' }}
            />
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col bg-tf-bg">
      {/* Main content area */}
      <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-tf-bg-muted via-tf-bg-card to-tf-bg-muted" />

        {/* Animated background orbs */}
        <div className="absolute top-1/4 -left-32 w-64 h-64 rounded-full bg-blue-500/5 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 rounded-full bg-purple-500/5 blur-3xl" />

        {/* Slide content */}
        <div className="relative z-10 w-full max-w-2xl">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlideIndex}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -100, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="px-4"
            >
              {renderSlide()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation arrows */}
        {currentSlideIndex > 0 && (
          <button
            onClick={prevSlide}
            className={cn(
              'absolute left-6 top-1/2 -translate-y-1/2 z-20',
              'w-12 h-12 rounded-full flex items-center justify-center',
              'bg-tf-bg-card/80 backdrop-blur border border-tf-border',
              'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-hover',
              'transition-all cursor-pointer'
            )}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {currentSlideIndex < TOTAL_SLIDES - 1 && (
          <button
            onClick={nextSlide}
            className={cn(
              'absolute right-6 top-1/2 -translate-y-1/2 z-20',
              'w-12 h-12 rounded-full flex items-center justify-center',
              'bg-tf-bg-card/80 backdrop-blur border border-tf-border',
              'text-tf-text-muted hover:text-tf-text hover:bg-tf-bg-hover',
              'transition-all cursor-pointer'
            )}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Navigation dots */}
      <div className="flex items-center justify-center gap-2 py-6">
        {Array.from({ length: TOTAL_SLIDES }).map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlideIndex(index)}
            className={cn(
              'transition-all duration-300 cursor-pointer',
              index === currentSlideIndex
                ? 'w-8 h-2 rounded-full bg-primary'
                : 'w-2 h-2 rounded-full bg-tf-border hover:bg-tf-text-muted'
            )}
          />
        ))}
      </div>

      {/* Keyboard hint */}
      <p className="text-center text-xs text-tf-text-muted pb-4">
        Use arrow keys to navigate
      </p>
    </div>
  )
}
